/** @file Page textures for a TexturePages table: premultiplied RGBA8, clamp-to-edge, linear|nearest, no mips. */
import type { TexturePages } from '@pose2d/geometry/types';
import type { RgbaImage } from '@pose2d/math/types';
import { invariant } from '@pose2d/std/invariant';
import { checkPageImages } from './checkPageImages';

/** GPU textures for the current page table, indexed like TexturePages.pages. */
export interface PageStore {
  /** Uploads images[i] for textures.pages[i]; the same image object with the same filter is kept as is. */
  upload(textures: TexturePages, images: readonly RgbaImage[]): void;
  /** Binds page `index` to TEXTURE_2D (unit 0); throws when that page has no texture. */
  bind(index: number): void;
  /** Number of pages with a texture. */
  count(): number;
  /** Deletes every texture; with `lost` only forgets them (the context already dropped them). */
  dispose(lost: boolean): void;
}

/** (Re)defines `texture` from a premultiplied image: RGBA8, clamp-to-edge, LINEAR or NEAREST, level 0 only. */
function writeTexture(gl: WebGL2RenderingContext, texture: WebGLTexture, image: RgbaImage, nearest: boolean): void {
  const filter = nearest ? gl.NEAREST : gl.LINEAR;
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  const bytes = new Uint8Array(image.data.buffer, image.data.byteOffset, image.data.byteLength);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, image.width, image.height, 0, gl.RGBA, gl.UNSIGNED_BYTE, bytes);
}

/** Creates an empty store; textures are made on the first upload of each page index. */
export function createPageStore(gl: WebGL2RenderingContext): PageStore {
  const textures: WebGLTexture[] = [];
  const sources: RgbaImage[] = [];
  const nearest: boolean[] = [];
  const truncate = (n: number, remove: boolean): void => {
    if (remove) for (const texture of textures.slice(n)) gl.deleteTexture(texture);
    textures.length = n;
    sources.length = n;
    nearest.length = n;
  };
  return {
    upload(pages, images) {
      checkPageImages(pages, images);
      const max = Number(gl.getParameter(gl.MAX_TEXTURE_SIZE));
      images.forEach((image, i) => {
        invariant(image.width <= max && image.height <= max, `uploadPages: image ${i} exceeds MAX_TEXTURE_SIZE ${max}`);
      });
      truncate(images.length, true);
      images.forEach((image, i) => {
        const near = pages.pages[i]?.filter === 'nearest';
        if (sources[i] === image && nearest[i] === near) return;
        const texture = textures[i] ?? gl.createTexture();
        textures[i] = texture;
        sources[i] = image;
        nearest[i] = near;
        writeTexture(gl, texture, image, near);
      });
    },
    bind(index) {
      const texture = textures[index];
      invariant(texture, `draw uses page ${index} but ${textures.length} pages are uploaded (uploadPages first)`);
      gl.bindTexture(gl.TEXTURE_2D, texture);
    },
    count: () => textures.length,
    dispose(lost) {
      truncate(0, !lost);
    },
  };
}
